import { HttpError } from '@/api/client'
import { createSyncStoragePersister } from '@tanstack/query-sync-storage-persister'
import { type Query, QueryClient } from '@tanstack/react-query'
import { persistQueryClient } from '@tanstack/react-query-persist-client'

// PERF-10 — heavy/volatile query families excluded from the localStorage
// snapshot. Event logs, timelines and analytics datasets are large and
// change constantly; persisting them risks blowing the ~5–10 MB browser
// quota (quota-exceeded silently corrupts the whole cache). Lists and
// detail views stay persisted so offline tracking/POD (charte §14) works.
const NON_PERSISTED_KEY_PARTS = ['events', 'timeline', 'analytics', 'audit', 'pdf', 'export']

function shouldPersistQuery(query: Query): boolean {
  if (query.state.status !== 'success') return false
  return !query.queryKey.some(
    (part) => typeof part === 'string' && NON_PERSISTED_KEY_PARTS.includes(part),
  )
}

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 60_000,
      gcTime: 24 * 60 * 60_000,
      retry: (failureCount, error) => {
        if (error instanceof HttpError && (error.status === 401 || error.status === 403)) {
          return false
        }
        return failureCount < 2
      },
      // PERF audit Lot 3.5 (2026-07-09): this default stays 'offlineFirst'
      // for cached list/detail views (offline tracking/POD, charte §14).
      // Auth-critical queries (auth.me, workspaces, preferences) override
      // to 'online' at their own useQuery() call — a client-wide default
      // can't distinguish them from everything else that shares this
      // QueryClient.
      networkMode: 'offlineFirst',
    },
    mutations: {
      networkMode: 'online',
    },
  },
})

if (typeof window !== 'undefined') {
  const persister = createSyncStoragePersister({
    storage: window.localStorage,
    key: 'amanet-query-cache',
  })

  void persistQueryClient({
    queryClient,
    persister,
    // PERF-10 — was 24h. A long window let the snapshot grow unbounded;
    // 1h keeps it small and fresh while still covering a working session.
    maxAge: 1000 * 60 * 60,
    dehydrateOptions: { shouldDehydrateQuery: shouldPersistQuery },
  })
}
